import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addSurplusResourcesMaintenance41_2bLCA,
  deleteSurplusResourcesMaintenance41_2bLCA,
  getAllSurplusResourcesMaintenance41_2bLCA,
  updateSurplusResourcesMaintenance41_2bLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import {
  getProductDisposalMethodMaintenance41_2bDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const SurplusResourcesMaintenance41_2bTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitDisposalMethods, setUnitDisposalMethods] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductDisposalMethodMaintenance41_2bDropdown({
      setDropdown: setDisposalMethodDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllSurplusResourcesMaintenance41_2bLCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        return prevData.map((item) => {
          if (item.country_surplus_material_treatment) {
            const foundCountry = countryDropdown.find(
              (country) =>
                country.code === item.country_surplus_material_treatment ||
                country.value === item.country_surplus_material_treatment
            );
            if (foundCountry) {
              return {
                ...item,
                country_surplus_material_treatment: foundCountry.name || foundCountry.value,
              };
            }
          }
          return item;
        });
      });
    }
  }, [data, countryDropdown]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && disposalMethodDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const disposalCode =
          typeof row.current_disposal_method === "string"
            ? row.current_disposal_method
            : row.current_disposal_method?.value || row.current_disposal_method?.code;
        if (disposalCode) {
          const selected = disposalMethodDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === disposalCode ||
                opt.value === disposalCode ||
                opt.name === disposalCode ||
                String(opt.value) === String(disposalCode))
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDisposalMethods(newUnits);
    }
  }, [data, disposalMethodDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      current_disposal_method: {
        targetField: "quantity_per_fu",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          _rowData,
          rowId = "new",
          fullOption
        ) => {
          if (
            fullOption &&
            typeof fullOption === "object" &&
            fullOption.unit != null &&
            fullOption.unit !== ""
          ) {
            setUnitDisposalMethods((prev) => ({
              ...prev,
              [rowId]: fullOption.unit,
            }));
            return;
          }
          const selected = dropdownData?.find(
            (opt) =>
              !opt.isCategory &&
              (opt?.value === selectedValue ||
                opt?.code === selectedValue ||
                opt?.name === selectedValue ||
                String(opt?.value) === String(selectedValue))
          );
          if (selected) {
            setUnitDisposalMethods((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "current_disposal_method",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const disposalCode =
        typeof editedItem.current_disposal_method === "string"
          ? editedItem.current_disposal_method
          : editedItem.current_disposal_method?.value ||
            editedItem.current_disposal_method?.code ||
            null;

      // Convert country name back to code for backend
      let countryCode = editedItem.country_surplus_material_treatment;
      if (countryCode) {
        if (typeof countryCode === "object") {
          countryCode = countryCode.code || countryCode.value || countryCode.name || null;
        } else if (typeof countryCode === "string" && countryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === countryCode || option.value === countryCode
          );
          if (foundCountry) {
            countryCode = foundCountry.code;
          }
        }
      }

      updateSurplusResourcesMaintenance41_2bLCA({
        productId,
        rowId: editedItem.id,
        name_maintenance_process: editedItem.name_maintenance_process || null,
        current_disposal_method: disposalCode,
        quantity_per_fu: editedItem.quantity_per_fu || 0,
        country_surplus_material_treatment: countryCode || null,
        waste_water_during_maintenance: editedItem.waste_water_during_maintenance || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const disposalCode =
        typeof newItem.current_disposal_method === "string"
          ? newItem.current_disposal_method
          : newItem.current_disposal_method?.value ||
            newItem.current_disposal_method?.code ||
            null;

      // Convert country name back to code for backend
      let countryCode = newItem.country_surplus_material_treatment;
      if (countryCode) {
        if (typeof countryCode === "object") {
          countryCode = countryCode.code || countryCode.value || countryCode.name || null;
        } else if (typeof countryCode === "string" && countryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === countryCode || option.value === countryCode
          );
          if (foundCountry) {
            countryCode = foundCountry.code;
          }
        }
      }

      addSurplusResourcesMaintenance41_2bLCA({
        productId,
        name_maintenance_process: newItem.name_maintenance_process || null,
        current_disposal_method: disposalCode,
        quantity_per_fu: newItem.quantity_per_fu || 0,
        country_surplus_material_treatment: countryCode || null,
        waste_water_during_maintenance: newItem.waste_water_during_maintenance || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteSurplusResourcesMaintenance41_2bLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  // Calculate weight from waste water (waste_water * 1000)
  const calculateWeight = useCallback((wasteWater) => {
    if (!wasteWater || isNaN(wasteWater)) return 0;
    return wasteWater * 1000;
  }, []);

  // Custom cell for Weight (auto-calculated, read-only)
  // This reads from rowEditedData if available (for real-time updates) or falls back to row.original
  const WeightCell = useCallback(
    ({ row, rowEditedData }) => {
      // First check if there's edited data (for real-time updates while typing)
      let wasteWater = rowEditedData?.waste_water_during_maintenance;
      
      // If no edited data, check current data state
      if (wasteWater === undefined || wasteWater === null) {
        const currentRowData = data.find(item => item.id === row.original.id);
        wasteWater = currentRowData?.waste_water_during_maintenance;
      }
      
      // Finally fall back to row.original
      if (wasteWater === undefined || wasteWater === null) {
        wasteWater = row.original.waste_water_during_maintenance || 0;
      }
      
      // Convert to number and ensure it's valid
      wasteWater = Number(wasteWater) || 0;
      
      const weight = calculateWeight(wasteWater);
      return <CustomInputComponent value={weight} readOnly />;
    },
    [calculateWeight, data]
  );

  const columns = useMemo(
    () => [
      {
        id: "name_maintenance_process",
        accessor: "name_maintenance_process",
        header: t("NAME_MAINTENANCE_PROCESS") || "Name maintenance process",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "current_disposal_method",
        accessor: "current_disposal_method",
        header: `${t("CURRENT_DISPOSAL_METHOD") || "Current disposal method"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DISPOSAL_METHOD") || "Select disposal method...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "current_disposal_method",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "quantity_per_fu",
        accessor: "quantity_per_fu",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "current_disposal_method",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "country_surplus_material_treatment",
        accessor: "country_surplus_material_treatment",
        header: t("COUNTRY_SURPLUS_MATERIAL_TREATMENT") || "Country of surplus material treatment",
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "waste_water_during_maintenance",
        accessor: "waste_water_during_maintenance",
        header: `${t("WASTE_WATER_DURING_MAINTENANCE") || "Waste water during maintenance"}`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
        unit: () => "m³",
      },
      {
        id: "weight",
        accessor: "weight",
        header: `${t("WEIGHT") || "Weight"}`,
        type: "custom",
        cell: WeightCell,
        optional: true,
        canSort: false,
        unit: () => "kg",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitDisposalMethods,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      WeightCell,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
      weight: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      current_disposal_method: disposalMethodDropdown || [],
      country_surplus_material_treatment: countryDropdown || [],
    }),
    [disposalMethodDropdown, countryDropdown]
  );

  // Custom input for Weight in new row
  const WeightForNewRow = useCallback(
    ({ value, onChange, rowData }) => {
      const wasteWater = rowData?.waste_water_during_maintenance || 0;
      const weight = calculateWeight(wasteWater);
      return <CustomInputComponent value={weight} readOnly />;
    },
    [calculateWeight]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
          weight: WeightForNewRow,
        }),
        [impactCategoryAccessor, WeightForNewRow]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default SurplusResourcesMaintenance41_2bTable;

